/**
 * iter-159 — invited-players picker for sectional rehearsals.
 *
 * Shown inside the Event dialog when event_type === "rehearsal_sectional".
 * Loads active member players (deps excluded — sectionals are for
 * the band's own musicians), groups them by section, and toggles
 * inclusion in `invited_player_ids`.
 */
import { useEffect, useMemo, useState } from "react";
import { apiClient } from "../App";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Users } from "lucide-react";

const InvitedPlayersPicker = ({ value = [], onChange, testidPrefix = "sectional" }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiClient.get("/players");
        if (cancelled) return;
        // Only active band members — deps, inactive and info-only
        // players don't play in sectionals.
        const list = (res.data || []).filter(
          (p) => p.player_type === "member" && p.is_active === true,
        );
        setPlayers(list);
      } catch {
        if (!cancelled) setPlayers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const p of players) {
      const names = p.section_names && p.section_names.length ? p.section_names : ["Unassigned"];
      for (const s of names) {
        if (!buckets.has(s)) buckets.set(s, []);
        buckets.get(s).push(p);
      }
    }
    // Sort section names alphabetically; Unassigned last.
    return [...buckets.entries()]
      .sort(([a], [b]) => {
        if (a === "Unassigned") return 1;
        if (b === "Unassigned") return -1;
        return a.localeCompare(b);
      })
      .map(([section, ps]) => [
        section,
        [...ps].sort((x, y) => (x.name || "").localeCompare(y.name || "")),
      ]);
  }, [players]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggle = (id) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const toggleSection = (ids, allSelected) => {
    if (allSelected) {
      onChange(value.filter((v) => !ids.includes(v)));
    } else {
      const merged = new Set([...value, ...ids]);
      onChange([...merged]);
    }
  };

  return (
    <div
      className="rounded-xl border border-primary/25 bg-primary/[0.04] p-4 space-y-3"
      data-testid={`${testidPrefix}-picker`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          <span className="font-medium text-sm">Invited players</span>
        </div>
        <Badge
          variant="outline"
          className="border-primary/30 text-primary bg-primary/10"
          data-testid={`${testidPrefix}-count`}
        >
          {value.length} selected
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        Only the players you tick here will be emailed about this sectional and see it on
        their events list. Deps aren't shown — invite them separately if needed.
      </p>

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading players…</p>
      ) : grouped.length === 0 ? (
        <p className="text-xs text-muted-foreground">No active members found.</p>
      ) : (
        <div className="max-h-64 overflow-y-auto space-y-3 pr-1">
          {grouped.map(([section, ps]) => {
            const ids = ps.map((p) => p.id);
            const allSelected = ids.every((id) => selectedSet.has(id));
            return (
              <div key={section}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    {section}
                  </p>
                  <button
                    type="button"
                    onClick={() => toggleSection(ids, allSelected)}
                    className="text-xs text-primary hover:underline"
                    data-testid={`${testidPrefix}-toggle-section-${section.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {allSelected ? "Clear section" : "Add all"}
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {ps.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-white/5 cursor-pointer"
                      data-testid={`${testidPrefix}-row-${p.id}`}
                    >
                      <Checkbox
                        checked={selectedSet.has(p.id)}
                        onCheckedChange={() => toggle(p.id)}
                      />
                      <span className="truncate">{p.name || p.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InvitedPlayersPicker;
